import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, ArrowLeft, Send, MessageSquare, CheckCheck, User, Briefcase, Calendar, Clock, Wifi, WifiOff, Plus, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import OfferCard from '../components/OfferCard';
import OfferForm from '../components/OfferForm';
import QuoteRequestForm from '../components/QuoteRequestForm';
import GetAppBanner from '../components/GetAppBanner';
import { useSiteContent } from '../hooks/useSiteContent';
import { isDayRateEligible } from '../utils/workerType';

import { BACKEND_URL } from '../config/api';
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const POLL_INTERVAL = 10000;
const RECONNECT_DELAY = 3000;
const TYPING_TIMEOUT = 3000;
const EVENT_DEDUP_TTL = 10000; // 10 seconds TTL for event deduplication

export default function Messaging() {
  const navigate = useNavigate();
  const location = useLocation();
  const { get } = useSiteContent();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  
  // Offer states
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null);
  const [conversationOffers, setConversationOffers] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [counterOfferData, setCounterOfferData] = useState(null);
  // Tracks whether we've already honoured an auto-open intent from nav state.
  const autoOpenHandledRef = useRef(false);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Event deduplication: track recently seen event_ids to ignore duplicates
  const seenEventsRef = useRef(new Set());
  // Refs for WebSocket callback functions (avoids circular deps)
  const handleWsMessageRef = useRef(null);
  const getTokenRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if event was already processed (deduplication)
  const isDuplicateEvent = useCallback((eventId) => {
    if (!eventId) return false; // No event_id = process normally
    
    if (seenEventsRef.current.has(eventId)) {
      return true; // Duplicate, skip it
    }
    
    // Mark as seen and auto-cleanup after TTL
    seenEventsRef.current.add(eventId);
    setTimeout(() => {
      seenEventsRef.current.delete(eventId);
    }, EVENT_DEDUP_TTL);
    
    return false;
  }, []);

  useEffect(() => {
    // Initial auth check - runs once on mount
    const initAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        // Inline initial conversation fetch (mount-only, avoids dep on fetchConversations)
        try {
          const convResp = await axios.get(`${BACKEND_URL}/api/messages/conversations`, {
            withCredentials: true
          });
          setConversations(convResp.data);
        } catch (convError) {
          console.error('Failed to fetch conversations');
        }
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  useEffect(() => {
    if (location.state?.recipient && user) {
      const recipient = location.state.recipient;
      const existingConv = conversations.find(
        c => c.partner?.user_id === recipient.user_id
      );
      if (existingConv) {
        setSelectedConversation(existingConv);
      } else {
        setSelectedConversation({
          partner: recipient,
          messages: []
        });
      }
    }
  }, [location.state, user, conversations]);

  // Fetch offers when conversation changes
  useEffect(() => {
    if (selectedConversation?.partner?.user_id) {
      fetchConversationOffers(selectedConversation.partner.user_id);
    }
  }, [selectedConversation?.partner?.user_id]);

  const connectWebSocket = useCallback((token) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/ws/chat/${token}`);
    
    ws.onopen = () => {
      setWsConnected(true);
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWsMessageRef.current?.(data);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (user) getTokenRef.current?.();
      }, RECONNECT_DELAY);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [user]);

  const getTokenAndConnect = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/ws-token`, {
        withCredentials: true
      });
      if (response.data.token) {
        connectWebSocket(response.data.token);
      }
    } catch (error) {
      console.error('Failed to get WebSocket token');
    }
  };

  const handleWebSocketMessage = (data) => {
    // Extract event_id for deduplication (backend now sends this)
    const eventId = data.event_id || data.payload?.event_id;
    
    // Skip duplicate events (same event_id seen within TTL window)
    if (isDuplicateEvent(eventId)) {
      // Duplicate event - silently skip
      return;
    }
    
    switch (data.type) {
      case 'new_message':
      case 'new_chat_message':
        // Also dedupe by message_id as fallback for chat messages
        const messageId = data.message?.message_id || data.message_id;
        if (messageId && isDuplicateEvent(`msg:${messageId}`)) {
          // Duplicate message - silently skip
          return;
        }
        
        setConversations(prevConvs => {
          const updatedConvs = [...prevConvs];
          const convIndex = updatedConvs.findIndex(
            c => c.partner?.user_id === data.message.sender_id
          );
          
          if (convIndex >= 0) {
            // Check if message already exists (extra safety)
            const existingMsgs = updatedConvs[convIndex].messages || [];
            if (existingMsgs.some(m => m.message_id === data.message.message_id)) {
              return prevConvs; // Already have this message
            }
            updatedConvs[convIndex] = {
              ...updatedConvs[convIndex],
              messages: [...existingMsgs, data.message],
              last_message_at: data.message.created_at
            };
          } else {
            fetchConversations();
          }
          
          return updatedConvs;
        });
        
        setSelectedConversation(prev => {
          if (prev?.partner?.user_id === data.message.sender_id) {
            // Check if message already exists
            const existingMsgs = prev.messages || [];
            if (existingMsgs.some(m => m.message_id === data.message.message_id)) {
              return prev; // Already have this message
            }
            return {
              ...prev,
              messages: [...existingMsgs, data.message]
            };
          }
          return prev;
        });
        
        setTypingUsers(prev => {
          const updated = { ...prev };
          delete updated[data.message.sender_id];
          return updated;
        });
        break;

      case 'typing':
      case 'typing_indicator':
        // Typing indicators are idempotent, no dedup needed
        if (data.is_typing) {
          setTypingUsers(prev => ({ ...prev, [data.user_id]: true }));
          setTimeout(() => {
            setTypingUsers(prev => {
              const updated = { ...prev };
              delete updated[data.user_id];
              return updated;
            });
          }, TYPING_TIMEOUT + 1000);
        } else {
          setTypingUsers(prev => {
            const updated = { ...prev };
            delete updated[data.user_id];
            return updated;
          });
        }
        break;

      case 'new_offer':
      case 'offer_sent':
      case 'offer_accepted':
      case 'offer_declined':
      case 'offer_countered':
        // Offer events trigger a refetch - idempotent, but dedupe by offer_id
        const offerId = data.offer_id || data.payload?.offer_id;
        if (offerId && isDuplicateEvent(`offer:${data.type}:${offerId}`)) {
          // Duplicate offer event - silently skip
          return;
        }
        if (selectedConversation?.partner?.user_id) {
          fetchConversationOffers(selectedConversation.partner.user_id);
        }
        break;

      case 'booking_confirmed':
      case 'booking_cancelled':
      case 'booking_updated':
        // Booking events - dedupe and refresh
        const bookingId = data.booking_id || data.payload?.booking_id;
        if (bookingId && isDuplicateEvent(`booking:${data.type}:${bookingId}`)) {
          return;
        }
        if (selectedConversation?.partner?.user_id) {
          fetchConversationOffers(selectedConversation.partner.user_id);
        }
        break;

      case 'messages_read':
      case 'message_read':
        setSelectedConversation(prev => {
          if (prev?.partner?.user_id === data.reader_id) {
            return {
              ...prev,
              messages: prev.messages.map(msg => ({
                ...msg,
                read: msg.recipient_id === data.reader_id ? true : msg.read
              }))
            };
          }
          return prev;
        });
        break;
      
      default:
        // Unknown event type - ignore
        break;
    }
  };

  // Keep refs in sync for WebSocket callbacks
  handleWsMessageRef.current = handleWebSocketMessage;
  getTokenRef.current = getTokenAndConnect;

  const sendTypingIndicator = (isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation?.partner?.user_id) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        recipient_id: selectedConversation.partner.user_id,
        is_typing: isTyping
      }));
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    
    const now = Date.now();
    if (!lastTypingSentRef.current || now - lastTypingSentRef.current > 2000) {
      sendTypingIndicator(true);
      lastTypingSentRef.current = now;
    }
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, TYPING_TIMEOUT);
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      await fetchConversations();
      startPolling();
      getTokenAndConnect();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/conversations`, {
        withCredentials: true
      });
      setConversations(response.data);
      
      if (selectedConversation) {
        const updated = response.data.find(
          c => c.partner?.user_id === selectedConversation.partner?.user_id
        );
        if (updated) {
          setSelectedConversation(updated);
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [selectedConversation]);

  const fetchConversationOffers = async (partnerId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/offers/conversation/${partnerId}`, {
        withCredentials: true
      });
      setConversationOffers(response.data.offers || []);
      setActiveOffer(response.data.activeOffer);
    } catch (error) {
      console.error('Failed to fetch offers');
    }
  };

  const startPolling = () => {
    pollIntervalRef.current = setInterval(() => {
      if (!wsConnected) {
        fetchConversations();
      }
    }, POLL_INTERVAL);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedConversation || sending) return;

    sendTypingIndicator(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    setSending(true);
    const tempMessage = message;
    setMessage('');

    try {
      await axios.post(
        `${BACKEND_URL}/api/messages`,
        {
          recipient_id: selectedConversation.partner.user_id,
          content: tempMessage
        },
        { withCredentials: true }
      );
      
      const newMsg = {
        sender_id: user.user_id,
        content: tempMessage,
        created_at: new Date().toISOString(),
        read: false
      };
      
      setSelectedConversation(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMsg]
      }));
      
      if (!wsConnected) {
        setTimeout(fetchConversations, 500);
      }
    } catch (error) {
      const realDetail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to send: ${realDetail}`);
      setMessage(tempMessage);
    } finally {
      setSending(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setShowOfferForm(false);
    setShowCounterForm(false);
    
    if (conv.partner?.user_id) {
      fetchConversationOffers(conv.partner.user_id);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'read',
          partner_id: conv.partner.user_id
        }));
      }
    }
  };

  // Offer handlers
  const handleCreateOffer = async (offerData) => {
    if (!selectedConversation?.partner?.user_id) return;

    setOfferLoading(true);
    const partnerId = selectedConversation.partner.user_id;
    try {
      await axios.post(
        `${BACKEND_URL}/api/offers/create`,
        {
          conversationId: partnerId,
          offer: offerData
        },
        { withCredentials: true }
      );
      toast.success('Offer sent!');
      setShowOfferForm(false);
      fetchConversationOffers(partnerId);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      // If the backend says there's a stuck active offer but we can't see
      // it in the UI (no activeOffer state), offer a 1-tap recovery.
      const stuckActiveOffer = detail.includes("active offer") && !activeOffer;
      if (stuckActiveOffer) {
        if (window.confirm(
          "Looks like a previous offer is stuck. Cancel it and send this new one?"
        )) {
          try {
            const res = await axios.post(
              `${BACKEND_URL}/api/offers/conversation/${partnerId}/cancel-active`,
              {},
              { withCredentials: true }
            );
            if (res.data?.cancelled) {
              // Retry create
              await axios.post(
                `${BACKEND_URL}/api/offers/create`,
                { conversationId: partnerId, offer: offerData },
                { withCredentials: true }
              );
              toast.success('Cleared stale offer and sent new one.');
              setShowOfferForm(false);
              fetchConversationOffers(partnerId);
              return;
            }
            toast.error('Could not find a stale offer to cancel — try refreshing.');
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Recovery failed');
          }
        }
      } else {
        toast.error(detail || 'Failed to send offer');
      }
    } finally {
      setOfferLoading(false);
    }
  };

  // Quote request handler — posts a structured first chat message for
  // subcontractors / price-work / teams / ltd company tradespeople.
  const handleSubmitQuoteRequest = async (quoteData) => {
    if (!selectedConversation?.partner?.user_id) return;
    setQuoteLoading(true);
    const partnerId = selectedConversation.partner.user_id;
    const lines = [
      '📋 Quote request',
      '',
      `Job: ${quoteData.title}`,
      `Location: ${quoteData.location}`,
      `Target start: ${quoteData.target_start_date}`,
      '',
      'Details:',
      quoteData.description,
    ];
    const content = lines.join('\n');

    try {
      await axios.post(
        `${BACKEND_URL}/api/messages`,
        { recipient_id: partnerId, content },
        { withCredentials: true }
      );
      toast.success('Quote request sent');
      setShowQuoteForm(false);
      // Optimistic append
      setSelectedConversation((prev) => ({
        ...prev,
        messages: [
          ...(prev?.messages || []),
          {
            sender_id: user?.user_id,
            content,
            created_at: new Date().toISOString(),
            read: false,
          },
        ],
      }));
      if (!wsConnected) {
        setTimeout(fetchConversations, 500);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to send quote request';
      toast.error(`Failed to send: ${detail}`);
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    setOfferLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/offers/${offerId}/action`,
        { action: 'ACCEPT' },
        { withCredentials: true }
      );
      toast.success('Job accepted. You can now view the full job details in Confirmed Jobs.', {
        duration: 6000,
      });
      fetchConversationOffers(selectedConversation.partner.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleDeclineOffer = async (offerId) => {
    if (!confirm('Are you sure you want to decline this offer?')) return;
    
    setOfferLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/offers/${offerId}/action`,
        { action: 'DECLINE' },
        { withCredentials: true }
      );
      toast.success('Offer declined');
      fetchConversationOffers(selectedConversation.partner.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline offer');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleOpenCounter = (offer) => {
    setCounterOfferData(offer.counter_offer || offer.offer);
    setShowCounterForm(true);
  };

  const handleSubmitCounter = async (counterData) => {
    if (!activeOffer) return;
    
    setOfferLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/offers/${activeOffer.offer_id}/action`,
        { 
          action: 'COUNTER',
          counterOffer: counterData
        },
        { withCredentials: true }
      );
      toast.success('Counter offer sent!');
      setShowCounterForm(false);
      fetchConversationOffers(selectedConversation.partner.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send counter');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleCancelOffer = async (offerId) => {
    if (!confirm('Are you sure you want to cancel this offer?')) return;
    
    setOfferLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/offers/${offerId}/action`,
        { action: 'CANCEL' },
        { withCredentials: true }
      );
      toast.success('Offer cancelled');
      fetchConversationOffers(selectedConversation.partner.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel offer');
    } finally {
      setOfferLoading(false);
    }
  };

  const getUnreadCount = (conv) => {
    if (!conv.messages || !user) return 0;
    return conv.messages.filter(
      m => m.sender_id !== user.user_id && !m.read
    ).length;
  };

  const getLastMessage = (conv) => {
    if (!conv.messages || conv.messages.length === 0) return null;
    return conv.messages[conv.messages.length - 1];
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
  };

  const isPartnerTyping = selectedConversation?.partner?.user_id && typingUsers[selectedConversation.partner.user_id];
  const isEmployer = user?.user_type === 'employer';
  const partnerWorkerType = selectedConversation?.partner?.worker_type;
  const partnerPreferredWorkType = selectedConversation?.partner?.preferred_work_type;
  const partnerIsDayRate = isDayRateEligible(partnerWorkerType, partnerPreferredWorkType);
  // Day-rate tradespeople get the structured offer flow.
  const canCreateOffer =
    isEmployer &&
    !activeOffer &&
    selectedConversation?.partner?.user_type === 'tradesperson' &&
    partnerIsDayRate;
  // Subcontractors / teams / ltd / price-work get a lightweight quote request.
  const canRequestQuote =
    isEmployer &&
    !activeOffer &&
    selectedConversation?.partner?.user_type === 'tradesperson' &&
    !partnerIsDayRate;

  // Honour autoOpenIntent from nav state once the conversation is selected.
  useEffect(() => {
    if (!selectedConversation?.partner?.user_id || autoOpenHandledRef.current) return;
    if (!isEmployer) return;
    const intent = location.state?.autoOpenIntent;
    if (!intent) return;
    // Don't auto-open if an offer is already pending — user needs to see it first.
    if (activeOffer) {
      autoOpenHandledRef.current = true;
      return;
    }
    if (intent === 'offer' && canCreateOffer) {
      setShowOfferForm(true);
      autoOpenHandledRef.current = true;
    } else if (intent === 'quote' && canRequestQuote) {
      setShowQuoteForm(true);
      autoOpenHandledRef.current = true;
    }
  }, [
    selectedConversation?.partner?.user_id,
    isEmployer,
    location.state?.autoOpenIntent,
    activeOffer,
    canCreateOffer,
    canRequestQuote,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="messaging-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-lg font-bold text-slate-900">Messages</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <div className="flex items-center gap-1 text-green-600 text-xs">
                <Wifi className="h-3 w-3" />
                <span className="hidden sm:inline">Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-slate-400 text-xs">
                <WifiOff className="h-3 w-3" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden max-w-7xl w-full mx-auto">
        {/* Conversations list */}
        <div className={`w-full md:w-80 bg-white border-r border-slate-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Conversations</h2>
            {!isEmployer && (
              <div className="mt-3">
                <GetAppBanner />
              </div>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No messages yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Start a conversation by contacting a tradesperson
                </p>
              </div>
            ) : (
              conversations.map((conv) => {
                const lastMsg = getLastMessage(conv);
                const unread = getUnreadCount(conv);
                const isTyping = conv.partner?.user_id && typingUsers[conv.partner.user_id];
                
                return (
                  <button
                    key={conv.partner?.user_id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-slate-50 border-b border-slate-100 text-left transition-colors ${
                      selectedConversation?.partner?.user_id === conv.partner?.user_id ? 'bg-orange-50' : ''
                    }`}
                    data-testid={`conversation-${conv.partner?.user_id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                      {conv.partner?.profile_image ? (
                        <img
                          src={conv.partner.profile_image}
                          alt={conv.partner.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900 truncate">{conv.partner?.name || 'Unknown'}</p>
                        {lastMsg && (
                          <span className="text-xs text-slate-400">
                            {formatTime(lastMsg.created_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isTyping ? (
                          <p className="text-sm text-orange-500 italic">{get('messaging.typing', 'typing...')}</p>
                        ) : lastMsg ? (
                          <p className="text-sm text-slate-500 truncate">
                            {lastMsg.sender_id === user?.user_id && (
                              <CheckCheck className={`h-3 w-3 inline mr-1 ${lastMsg.read ? 'text-blue-500' : 'text-slate-400'}`} />
                            )}
                            {lastMsg.message_type === 'OFFER' ? '📋 Offer sent' : lastMsg.content}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400">{get('empty.no_messages', 'No messages yet')}</p>
                        )}
                        {unread > 0 && (
                          <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className={`flex-1 flex flex-col bg-slate-50 ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          {selectedConversation ? (
            <>
              {/* Chat header */}
              <div className="bg-white border-b border-slate-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <ArrowLeft className="h-5 w-5 text-slate-600" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    {selectedConversation.partner?.profile_image ? (
                      <img
                        src={selectedConversation.partner.profile_image}
                        alt={selectedConversation.partner.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedConversation.partner?.name}</p>
                    {isPartnerTyping ? (
                      <p className="text-xs text-orange-500 italic">{get('messaging.typing', 'typing...')}</p>
                    ) : (
                      <p className="text-xs text-slate-500">
                        {selectedConversation.partner?.user_type === 'tradesperson' ? 'Tradesperson' : 'Employer'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Create Offer CTA Bar (for employers without active offer) */}
              {canCreateOffer && !showOfferForm && (
                <div className="bg-orange-50 border-b border-orange-200 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-orange-500" />
                    <span className="text-sm text-orange-700">Ready to hire? Create a structured offer.</span>
                  </div>
                  <button
                    onClick={() => setShowOfferForm(true)}
                    className="btn-primary text-sm py-1.5 px-4 flex items-center gap-1"
                    data-testid="create-offer-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Send Job Offer
                  </button>
                </div>
              )}

              {/* Quote Request CTA Bar (subcontractors / teams / ltd / price-work) */}
              {canRequestQuote && !showQuoteForm && (
                <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-center justify-between" data-testid="quote-cta-bar">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-amber-600" />
                    <span className="text-sm text-amber-800">Need a price? Send a quote request.</span>
                  </div>
                  <button
                    onClick={() => setShowQuoteForm(true)}
                    className="btn-primary text-sm py-1.5 px-4 flex items-center gap-1"
                    data-testid="request-quote-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Request Quote
                  </button>
                </div>
              )}

              {/* Offer Form Modal */}
              <Dialog open={showOfferForm} onOpenChange={setShowOfferForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-orange-500" />
                      Send Job Offer
                    </DialogTitle>
                  </DialogHeader>
                  <OfferForm
                    onSubmit={handleCreateOffer}
                    onCancel={() => setShowOfferForm(false)}
                    loading={offerLoading}
                  />
                </DialogContent>
              </Dialog>

              {/* Quote Request Modal (non-day-rate engagement) */}
              <Dialog open={showQuoteForm} onOpenChange={setShowQuoteForm}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="quote-request-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-amber-500" />
                      Request a Quote
                    </DialogTitle>
                  </DialogHeader>
                  <QuoteRequestForm
                    onSubmit={handleSubmitQuoteRequest}
                    onCancel={() => setShowQuoteForm(false)}
                    loading={quoteLoading}
                  />
                </DialogContent>
              </Dialog>

              {/* Counter Offer Modal */}
              <Dialog open={showCounterForm} onOpenChange={setShowCounterForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-amber-500" />
                      {user?.user_type === 'tradesperson'
                        ? 'Counter the offer (dates & day rate only)'
                        : 'Counter Offer'}
                    </DialogTitle>
                    {user?.user_type === 'tradesperson' && (
                      <p className="text-sm text-slate-600 mt-1">
                        Propose new dates or a different day rate. The address and job scope stay as the employer set them.
                      </p>
                    )}
                  </DialogHeader>
                  <OfferForm
                    onSubmit={handleSubmitCounter}
                    onCancel={() => setShowCounterForm(false)}
                    initialData={counterOfferData}
                    isCounter={true}
                    counterRole={user?.user_type === 'tradesperson' ? 'tradesperson' : 'employer'}
                    loading={offerLoading}
                  />
                </DialogContent>
              </Dialog>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Active Offer Card (pinned at top) */}
                {activeOffer && (
                  <div className="mb-4">
                    <OfferCard
                      offer={activeOffer}
                      currentUserId={user?.user_id}
                      currentUserType={user?.user_type}
                      onAccept={handleAcceptOffer}
                      onCounter={handleOpenCounter}
                      onDecline={handleDeclineOffer}
                      onCancel={handleCancelOffer}
                    />
                  </div>
                )}

                {/* Message history */}
                {selectedConversation.messages?.map((msg, idx) => {
                  const isOwn = msg.sender_id === user?.user_id;
                  const isSystemMessage = msg.message_type === 'SYSTEM' || msg.message_type === 'SYSTEM_STATUS';
                  const isOfferMessage = msg.message_type === 'OFFER';
                  
                  // Skip offer messages as they're shown in the card
                  if (isOfferMessage) {
                    return (
                      <div key={idx} className="flex justify-center">
                        <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                          {isOwn ? 'You sent an offer' : 'Offer received'}
                        </span>
                      </div>
                    );
                  }
                  
                  if (isSystemMessage) {
                    return (
                      <div key={idx} className="flex justify-center">
                        <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                          {msg.content.replace('[SYSTEM] ', '')}
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={idx}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          isOwn
                            ? 'bg-orange-500 text-white rounded-br-md'
                            : 'bg-white text-slate-900 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-orange-100' : 'text-slate-400'}`}>
                          <span className="text-xs">
                            {formatTime(msg.created_at)}
                          </span>
                          {isOwn && (
                            <CheckCheck className={`h-3 w-3 ${msg.read ? 'text-white' : 'text-orange-200'}`} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Typing indicator */}
                {isPartnerTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Message input */}
              <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-slate-200">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={!message.trim() || sending}
                    className="btn-primary p-3 rounded-full disabled:opacity-50"
                    data-testid="send-btn"
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">{get('messaging.empty.select_conversation', 'Select a conversation')}</h3>
                <p className="text-slate-500 mt-1">{get('messaging.empty.select_conversation_desc', 'Choose a conversation from the list to view messages.')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
